import React from 'react';
import { useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';
import type { MailboxProviderId } from 'fym-shared/src/music24/outreach/mailboxProviders';
import { TypedMemo } from '../../../common/types';
import { MAILBOX_GUIDE_COPY } from './mailboxGuideCopy';
import { mailboxGuideMessages as messages } from './mailboxGuideMessages';
import { ProviderMark } from './ProviderMark';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the guide; each part uses its own slice of it */
import styles from './MailboxGuide.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The three ways forward of the card (design outreach-v2 p4 `.nos-alts`). */
const WAYS: ReadonlyArray<{ id: MailboxProviderId; name: MessageDescriptor; sub: MessageDescriptor }> = [
  { id: 'gmail', name: MAILBOX_GUIDE_COPY.gmail.label, sub: messages.unsupportedGmailSub },
  { id: 'other', name: messages.unsupportedHostName, sub: messages.unsupportedHostSub },
  { id: 'icloud', name: MAILBOX_GUIDE_COPY.icloud.label, sub: messages.unsupportedIcloudSub },
];

interface UnsupportedCardProps {
  /** Outlook, Microsoft 365 or another provider whose `passwordKind` is `unsupported`. */
  providerId: MailboxProviderId;
  /** A tap on a way forward: the guide clears the address, picks that tile and focuses the field. */
  onPickWay: (id: MailboxProviderId) => void;
}

/**
 * One calm card for a mailbox that cannot send pitches (design outreach-v2 p4). The reason is the
 * guide intro of the provider (`MICROSOFT_UNSUPPORTED` for Outlook and Microsoft 365).
 */
const UnsupportedCardComponent: React.FC<UnsupportedCardProps> = ({ providerId, onPickWay }) => {
  const intl = useIntl();
  const copy = MAILBOX_GUIDE_COPY[providerId];
  return (
    <div className={styles.unsupported} data-testid="mailbox-unsupported">
      <div className="d-flex align-items-center gap-3">
        <ProviderMark providerId={providerId} />
        <b className={styles.unsupportedTitle}>
          {intl.formatMessage(messages.unsupportedTitle, { provider: intl.formatMessage(copy.label) })}
        </b>
      </div>
      <p className="mb-0">{intl.formatMessage(copy.guideIntro)}</p>
      <div className={styles.ways}>
        {WAYS.map((way) => (
          <button
            key={way.id}
            type="button"
            className={styles.tile}
            onClick={() => onPickWay(way.id)}
            data-testid={`mailbox-way-${way.id}`}
          >
            <ProviderMark providerId={way.id} />
            <span className={styles.tileText}>
              <b>{intl.formatMessage(way.name)}</b>
              <small>{intl.formatMessage(way.sub)}</small>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export const UnsupportedCard = TypedMemo(UnsupportedCardComponent);
